import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../styles/pages/AccessPage.scss';
import '../../styles/animations/button-slide.scss';
import '../../styles/animations/shrink-and-center.scss';

import AccessPageVideo from '../animations/AccessPageVideo.tsx';
import FloatingAstronaut from '../animations/FloatingAstronaut.tsx';

import setPageTitle from '../../utils/setPageTitle.ts';
import isIOS from '../../utils/isIOS.ts';

const homePageRoute = '/homepage';

const AstronautStyleProps = {
  animationSpeed: 30,
  maxWidth: 150,
  move: {
    fromTop: 0,
    toTop: 300,
    fromLeft: -100,
    toLeft: 100,
    fromRight: 0,
    toRight: 0,
  },
  rotation: {
    rotate: -20,
  },
};

function AccessPage(): React.ReactElement {
  const $accessPageMain = useRef<HTMLElement>(null);
  const $videoElement = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();

  setPageTitle('Access Arthur\'s Portfolio 👨‍🚀');

  const handleVideoEnd = () => {
    if ($videoElement.current) {
      $videoElement.current.style.display = 'none';
      $videoElement.current.style.visibility = 'hidden';
      $videoElement.current.style.opacity = '0';
      $videoElement.current.style.zIndex = '-1';
    }

    navigate(homePageRoute);
  };

  const handleMainElementAnimations = () => {
    if ($accessPageMain.current) {
      $accessPageMain.current.classList.remove('text-hue-rotate');
      $accessPageMain.current.classList.add('shrink-and-center');
    }
  };

  const handleVideoInteraction = () => {
    if (isIOS()) {
      navigate(homePageRoute);
      return;
    }

    const video = $videoElement.current;

    if (video) {
      video.style.visibility = 'visible';
      video.style.opacity = '1';
      video.style.zIndex = '10';
      video.style.display = 'block';

      video.muted = true;
      video.playsInline = true;

      video.removeEventListener('ended', handleVideoEnd);
      video.addEventListener('ended', handleVideoEnd, { once: true });

      const playPromise = video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setPageTitle('🚀..........');
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Video play failed:', error);
            navigate(homePageRoute);
          });
      } else {
        navigate(homePageRoute);
      }
    } else {
      navigate(homePageRoute);
    }
  };

  return (
    <div className="blend-in-out">
      <FloatingAstronaut
        animationSpeed={AstronautStyleProps.animationSpeed}
        maxWidth={AstronautStyleProps.maxWidth}
        move={AstronautStyleProps.move}
        rotation={AstronautStyleProps.rotation}
      />
      <main ref={$accessPageMain} className="access-page-main">
        <span>Arthur Iturres</span>
        <h1>Delivering The Future</h1>
        <div className="access-page-main__content">
          <button
            type="button"
            className="my-btn color2 slide-up"
            onClick={() => {
              handleMainElementAnimations();
              handleVideoInteraction();
            }}
          >
            Access
          </button>

          <em className="access-page-main__note slide-up">
            * Please note: the following short &quot;Access&quot; animation is
            only available on non-iOS devices and may cause discomfort to users
            sensitive to motion or flashing visuals. &nbsp;
            <button
              type="button"
              className="secondary-btn"
              onClick={() => {
                navigate(homePageRoute);
              }}
            >
              Go directly to the homepage.
            </button>
          </em>
        </div>
      </main>
      <AccessPageVideo $videoElement={$videoElement} />
    </div>
  );
}

export default AccessPage;
